import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { usePageMeta } from "@/hooks/use-page-meta";
import { Button } from "@/components/ui/button";
import { Trash2, Mail, Clock, ShieldCheck } from "lucide-react";

export default function DeleteAccount() {
  usePageMeta({
    title: "Delete Account & Data – Buukd",
    description:
      "Request deletion of your Buukd account and all associated personal data.",
    path: "/delete-account",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />

      <main className="flex-1 pt-24 pb-12">
        <div className="container-app max-w-2xl">
          <div className="flex items-center gap-3 mb-2">
            <Trash2 className="h-8 w-8 text-destructive" />
            <h1 className="text-4xl font-bold text-foreground">
              Delete Account &amp; Data
            </h1>
          </div>
          <p className="text-muted-foreground mb-10">
            Last Updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}
          </p>

          <div className="space-y-8 text-foreground">
            <section>
              <p className="text-muted-foreground leading-relaxed">
                You have the right to request the deletion of your Buukd account
                and all personal data associated with it. This includes your
                profile information, business details, booking history,
                customer records, and any other data we hold on your behalf.
              </p>
            </section>

            <section className="bg-muted/50 rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold">What gets deleted</h2>
              <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                <li>Your Buukd account and login credentials</li>
                <li>Your business profile and settings</li>
                <li>All appointments, bookings, and scheduling history</li>
                <li>Customer records and contact information you uploaded</li>
                <li>Membership and subscription data</li>
                <li>Invoices, orders, and payment records you created</li>
                <li>Staff accounts linked to your business</li>
              </ul>
              <p className="text-sm text-muted-foreground pt-2">
                <strong>Note:</strong> We may retain certain records for a
                limited period where required by law (e.g. financial records for
                tax compliance), after which they will be permanently deleted.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold">How to request deletion</h2>
              <p className="text-muted-foreground leading-relaxed">
                Send an email to our support team from the email address
                associated with your Buukd account. Include your full name and
                the word <strong>"DELETE"</strong> in the subject line so we can
                process it promptly.
              </p>

              <a
                href="mailto:hello@buukd.co?subject=DELETE%20My%20Account%20and%20Data&body=Hi%20Buukd%20Team%2C%0A%0AI%20would%20like%20to%20request%20the%20permanent%20deletion%20of%20my%20account%20and%20all%20associated%20data.%0A%0AName%3A%20%5BYour%20full%20name%5D%0AEmail%3A%20%5BYour%20account%20email%5D%0A%0AThank%20you."
              >
                <Button variant="destructive" size="lg" className="gap-2">
                  <Mail className="h-4 w-4" />
                  Email hello@buukd.co to Request Deletion
                </Button>
              </a>
            </section>

            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border rounded-lg p-5 space-y-2">
                <div className="flex items-center gap-2 text-foreground font-medium">
                  <Clock className="h-4 w-4 text-primary" />
                  Response time
                </div>
                <p className="text-sm text-muted-foreground">
                  We will confirm receipt within 2 business days and complete
                  the deletion within 30 days of your verified request.
                </p>
              </div>
              <div className="border rounded-lg p-5 space-y-2">
                <div className="flex items-center gap-2 text-foreground font-medium">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Verification
                </div>
                <p className="text-sm text-muted-foreground">
                  We will verify your identity by confirming the request comes
                  from the email address registered to the account.
                </p>
              </div>
            </section>

            <section>
              <p className="text-sm text-muted-foreground">
                For any questions about this process, contact us at{" "}
                <a
                  href="mailto:hello@buukd.co"
                  className="underline hover:text-foreground"
                >
                  hello@buukd.co
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
